import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import { alpha, useTheme } from '@mui/material/styles';

export interface MarkdownTableProps {
  children?: ReactNode;
  /** Applies compact padding; useful for very wide tables. */
  dense?: boolean;
}

/**
 * Scroll container for Markdown tables.
 *
 * Wide tables scroll horizontally instead of forcing the page to, and edge
 * shadows signal that there is more content off-screen.
 */
export const MarkdownTable = memo(function MarkdownTable({ children, dense }: MarkdownTableProps) {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    const { scrollLeft, scrollWidth, clientWidth } = element;
    setEdges({
      start: scrollLeft > 2,
      end: scrollLeft + clientWidth < scrollWidth - 2,
    });
  }, []);

  useEffect(() => {
    measure();
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  return (
    <Box sx={{ position: 'relative', my: 2.5 }}>
      <TableContainer
        component={Paper}
        variant="outlined"
        ref={ref}
        onScroll={measure}
        sx={{ overflowX: 'auto', borderRadius: `${theme.shape.borderRadius}px` }}
      >
        <Table
          size={dense ? 'small' : 'medium'}
          sx={{
            width: '100%',
            minWidth: 'max-content',
            '& th, & td': {
              borderColor: theme.palette.divider,
              fontSize: '0.9rem',
              lineHeight: 1.6,
            },
            '& thead th': {
              fontWeight: 700,
              whiteSpace: 'nowrap',
              backgroundColor: theme.markdown.tableHeader,
              color: theme.palette.text.primary,
            },
            '& tbody tr': {
              transition: theme.transitions.create('background-color', { duration: 120 }),
            },
            '& tbody tr:nth-of-type(even)': { backgroundColor: theme.markdown.tableStripe },
            '& tbody tr:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
            '& tbody tr:last-child td': { borderBottom: 0 },
            '& p': { margin: 0 },
            '& code': { whiteSpace: 'nowrap' },
          }}
        >
          {children}
        </Table>
      </TableContainer>

      {(['start', 'end'] as const).map((side) => (
        <Box
          key={side}
          aria-hidden
          sx={{
            position: 'absolute',
            top: 1,
            bottom: 1,
            [side === 'start' ? 'left' : 'right']: 1,
            width: 28,
            pointerEvents: 'none',
            opacity: edges[side] ? 1 : 0,
            transition: theme.transitions.create('opacity', { duration: 150 }),
            background: `linear-gradient(to ${side === 'start' ? 'right' : 'left'}, ${alpha(
              theme.palette.background.paper,
              0.95,
            )}, transparent)`,
          }}
        />
      ))}
    </Box>
  );
});

export default MarkdownTable;
